import React, { useState } from "react";
import { DeleteProject } from "../Delete Project";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { Docs } from "../../../../redux/types";
import { useLazyQuery } from "@apollo/client";
import { ADMIN_CUSTOMER_PASSWORD_RESET_QUERY } from "../../../../apolloClient/operation/querys/userQuery";
import { toast } from "react-toastify";
import { Box, Button, Typography, CircularProgress } from "@mui/material";

interface CustomerProjectProps {
  project: CustomerProjectDetails;
  docs?: Docs[];
  onDelete: Function;
  permissions?: boolean;
}

const BRANDING_SOURCES = [
  { title: "Glas", value: "glas" },
  { title: "Waybe", value: "waybe" },
];

export const Settings: React.FC<CustomerProjectProps> = ({
  project,
  docs,
  onDelete,
  permissions,
}) => {
  const [source, setSource] = useState("glas");
  const [resetPassword, { loading }] = useLazyQuery(ADMIN_CUSTOMER_PASSWORD_RESET_QUERY);

  const handleResetPassword = async () => {
    if (!project?.customerDetails?.email) {
      toast.error("Customer email is missing.");
      return;
    }
    
    try {
      const { data } = await resetPassword({
        variables: {
          email: project.customerDetails.email,
          source: source,
        },
      });
      
      if (data?.adminCustomerPasswordReset) {
        toast.success("Password reset link sent successfully!");
      } else {
        toast.error("Failed to send password reset link.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while sending the password reset link.");
    }
  };

  return (
    <div>
      <h1 className="mb-8 font-normal text-xl">Settings</h1>
      
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "stretch" }}>
        
        {/* Customer Password Reset Section */}
        <Box sx={{ flex: 1, minWidth: "350px", p: 4, border: "1px solid #e5e7eb", borderRadius: "12px", backgroundColor: "#fff", display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: 18, fontWeight: 600, color: "#1f2937" }}>
            Customer Password Reset
          </Typography>
          <Typography sx={{ mb: 4, color: "#4b5563", fontSize: 14 }}>
            Send a password reset email to the customer ({project?.customerDetails?.email || "No email"}). Select the branding source for the email below.
          </Typography>
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: "auto" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
              Email Branding Source
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: "150px" }}>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  style={{
                    width: "100%",
                    height: "50px",
                    padding: "0 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "#fff",
                    fontSize: "15px",
                    color: "#374151",
                    outline: "none",
                    cursor: "pointer",
                    boxSizing: "border-box"
                  }}
                >
                  {BRANDING_SOURCES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.title}
                    </option>
                  ))}
                </select>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleResetPassword}
                disabled={loading || !project?.customerDetails?.email}
                sx={{ height: 50, px: 3, textTransform: "none", borderRadius: "8px", boxShadow: "none" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Delete Project Section */}
        {permissions && (
          <Box sx={{ flex: 1, minWidth: "350px", display: "flex", flexDirection: "column" }}>
            <DeleteProject project={project} docs={docs} onDelete={onDelete} />
          </Box>
        )}
      </Box>
    </div>
  );
};

